import { WarrantyServiceType, WarrantyWork as WarrantyWorkType } from '@api/types';
import { TextCodes } from '@api/types/translation/TextCodes';
import { DropdownField, DropdownOption, Title, useWarrantyClaimContext } from '@components';
import { useAuth } from '@hooks';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { Box, Divider, IconButton, Stack, TextField, TextFieldProps } from '@mui/material';
import { colors } from '@themes/colors';
import { t } from '@translations';
import { calculateTotalPrice } from '@utils/price.utils';
import { Controller, useFieldArray } from 'react-hook-form';

type WorkField = {
  name: keyof WarrantyWorkType;
  options?: DropdownOption<WarrantyServiceType>[];
} & Omit<TextFieldProps, 'name'>;

type WarrantyWorkListViewProps = {
  fields: WarrantyWorkType[];
  handleEdit: (index: number, name: keyof WarrantyWorkType, value: string) => void;
  addItem: () => void;
  removeItem: (index: number) => void;
};

type WorkListProps = {
  fields: WarrantyWorkType[];
  handleEdit: (index: number, name: keyof WarrantyWorkType, value: string) => void;
  removeItem: (index: number) => void;
  isBeremaUser?: boolean;
};

const serviceTypes = (): DropdownOption<WarrantyServiceType>[] => [
  {
    label: t(TextCodes.Warranty.Block.Labor),
    value: 'labor',
  },
  {
    label: t(TextCodes.Warranty.Block.Freight),
    value: 'freight',
  },
  {
    label: t(TextCodes.Warranty.Block.Diverse),
    value: 'diverse',
  },
  {
    label: t(TextCodes.Warranty.Block.Troubleshooting),
    value: 'troubleshooting',
  },
];

const workFields = ({
  selectedServiceType,
  isBeremaUser,
  isReadOnly,
}: {
  selectedServiceType?: WarrantyServiceType;
  isBeremaUser?: boolean;
  isReadOnly?: boolean;
}): WorkField[] => [
  {
    label: t(TextCodes.Warranty.Block.SelectServiceType),
    name: 'serviceTypes',
    options: serviceTypes(),
  },
  {
    label: t(TextCodes.Warranty.Block.Price),
    name: 'price',
    type: 'number',
    InputProps: {
      inputProps: { min: 0.0, inputMode: 'decimal', step: 0.01 },
      readOnly:
        !isBeremaUser &&
        (selectedServiceType === 'troubleshooting' || selectedServiceType === 'labor'),
    },
  },
  {
    label: t(TextCodes.Warranty.Block.Quantity),
    name: 'quantity',
    type: 'number',
    InputProps: {
      inputProps: { min: 0.0, inputMode: 'decimal', step: 0.1 },
      sx: {
        maxWidth: '100px',
      },
      readOnly: isReadOnly,
    },
  },
  {
    label: t(TextCodes.Warranty.Block.TotalPrice),
    name: 'totalPrice',
    InputProps: { readOnly: true },
  },
];

const serviceTypeToPartNo = {
  labor: '510',
  freight: '515',
  diverse: '535',
  troubleshooting: '575',
};

export const WarrantyWorkListSection = () => {
  const { form } = useWarrantyClaimContext();
  const troubleshootingPrice = form.watch('troubleshootingPrice');
  const laborPrice = form.watch('laborPrice');
  const { fields, remove, update } = useFieldArray({
    control: form.control,
    name: 'works',
  });

  const handleEdit = (index: number, name: keyof WarrantyWorkType, value: string) => {
    const updatedWork = { ...fields[index], [name]: value };
    if (name === 'price' || name === 'quantity') {
      const totalPrice = calculateTotalPrice(updatedWork.price || 0, updatedWork.quantity || 1);
      updatedWork.totalPrice = totalPrice;
    }
    if (name === 'serviceTypes') {
      const partNo = serviceTypeToPartNo[value as keyof typeof serviceTypeToPartNo];
      updatedWork.partNo = partNo;
      if (value === 'troubleshooting') {
        const totalPrice = calculateTotalPrice(
          troubleshootingPrice || 0,
          updatedWork.quantity || 1
        );
        updatedWork.price = troubleshootingPrice;
        updatedWork.totalPrice = totalPrice;
        form.clearErrors(`works.${index}.price`);
      }
      if (value === 'labor') {
        const totalPrice = calculateTotalPrice(laborPrice || 0, updatedWork.quantity || 1);
        updatedWork.price = laborPrice;
        updatedWork.totalPrice = totalPrice;
        form.clearErrors(`works.${index}.price`);
      }
      if (value !== 'labor' && value !== 'troubleshooting') {
        updatedWork.price = undefined;
        updatedWork.totalPrice = undefined;
      }

      if (value) {
        const fieldName = `works.${index}.${name}` as const;
        form.clearErrors(fieldName);
      }
    }

    if (updatedWork.id === 0) {
      update(index, { ...updatedWork, id: 0 });
    } else {
      update(index, { ...updatedWork, id: form.watch('works')?.[index].id });
    }
  };

  const addItem = () => {
    form.setValue('works', [...(form.watch('works') || []), { quantity: 1, id: 0 }]);
  };

  return (
    <WarrantyWorkListView
      fields={fields}
      handleEdit={handleEdit}
      addItem={addItem}
      removeItem={remove}
    />
  );
};

const WarrantyWorkListView = ({
  fields,
  handleEdit,
  addItem,
  removeItem,
}: WarrantyWorkListViewProps) => {
  const { isReadOnly } = useWarrantyClaimContext();
  return (
    <>
      <Box display='flex' alignItems='center'>
        <Title noDot variant='h3' sx={{ mr: 2 }}>
          {t(TextCodes.Warranty.Block.Work)} / {t(TextCodes.Warranty.Block.OtherCost)}
        </Title>
        {!isReadOnly && (
          <IconButton onClick={addItem} color='success'>
            <AddCircleOutlineIcon />
          </IconButton>
        )}
      </Box>
      <WorkList fields={fields} handleEdit={handleEdit} removeItem={removeItem} />
    </>
  );
};

const WorkList = ({ fields, handleEdit, removeItem }: WorkListProps) => {
  const { form, isReadOnly } = useWarrantyClaimContext();
  const { isBeremaUser } = useAuth();
  const works = form.watch('works');
  return (
    <Stack direction='column' spacing={2} sx={{ mt: 4 }}>
      {fields.length === 0 ? (
        <Box fontStyle='italic' color='text.secondary'>
          {t(TextCodes.Warranty.Block.NoItemsAdded)}
        </Box>
      ) : (
        fields.map((row, index) => (
          <Box
            key={index}
            display='flex'
            flexDirection={{
              xs: 'column',
              sm: 'row',
            }}
            alignItems='center'
          >
            <Box
              sx={{
                width: {
                  xs: '100%',
                  sm: 'auto',
                },
              }}
            >
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={{ xs: 4, sm: 2 }}
                width={{ xs: '100%', sm: 'auto' }}
              >
                {workFields({
                  selectedServiceType: row.serviceTypes,
                  isBeremaUser,
                  isReadOnly,
                }).map((workField) =>
                  workField.options ? (
                    <Box
                      key={workField.name}
                      sx={{
                        '& label': {
                          display: { xs: 'block', sm: index === 0 ? 'block' : 'none' },
                        },
                      }}
                    >
                      <Controller
                        control={form.control}
                        name={`works.${index}.${workField.name}`}
                        rules={{ required: true }}
                        render={({ field, fieldState: { error } }) => {
                          return (
                            <DropdownField
                              name={field.name}
                              options={workField.options || []}
                              getValue={(value) =>
                                handleEdit(index, workField.name, value as string)
                              }
                              value={row[workField.name] || ''}
                              label={workField.label as string}
                              error={!!error}
                              readOnly={isReadOnly}
                            />
                          );
                        }}
                      />
                    </Box>
                  ) : (
                    <Controller
                      key={workField.name}
                      control={form.control}
                      name={`works.${index}.${workField.name}`}
                      rules={{ required: workField.name !== 'totalPrice' }}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          {...workField}
                          sx={{
                            width: { xs: '100%', sm: 'auto' },
                            '& label': {
                              display: { xs: 'block', sm: index === 0 ? 'block' : 'none' },
                            },
                          }}
                          label={workField.label}
                          value={works?.[index]?.[workField.name] || ''}
                          onChange={(e) => {
                            handleEdit(index, workField.name, e.target.value);
                            field.onChange(e);
                          }}
                          error={!!error}
                        />
                      )}
                    />
                  )
                )}
              </Stack>
              {row.serviceTypes === 'diverse' && (
                <Box color={colors.secondary.darkBlueGrey} mt={1} fontStyle='italic'>
                  {t(TextCodes.Warranty.Block.SpecifyMoreInformation)}
                </Box>
              )}
            </Box>

            {!isReadOnly && (
              <IconButton
                onClick={() => removeItem(index)}
                color='error'
                sx={{
                  alignSelf: { xs: 'center', sm: 'baseline' },
                  ml: { xs: 0, sm: 2 },
                  my: { xs: 2, sm: 0 },
                }}
              >
                <RemoveCircleOutlineIcon />
              </IconButton>
            )}

            <Divider
              sx={{
                display: { xs: 'block', sm: 'none' },
                width: '100%',
                mb: 2,
              }}
            />
          </Box>
        ))
      )}
      <WorkTotalPrice fields={fields} />
    </Stack>
  );
};

const WorkTotalPrice = ({ fields }: { fields: WarrantyWorkType[] }) => {
  const { form } = useWarrantyClaimContext();
  const workFields = form.watch('works');

  const totalPrice = workFields?.reduce((acc, work) => {
    const workTotalPrice = work.price && work.quantity ? work.price * work.quantity : 0;
    return acc + workTotalPrice;
  }, 0);

  return fields.length ? (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems='center'>
      <Box>{t(TextCodes.Warranty.Block.TotalPrice)}:</Box>
      <TextField
        value={totalPrice ? (+totalPrice).toFixed(2) : '0.00'}
        InputProps={{
          readOnly: true,
        }}
      />
    </Stack>
  ) : null;
};
